import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Barcode as BarcodeIcon, Minus, Plus, Check, ImageBroken, MagnifyingGlass } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "../lib/api";
import { MobileHeader } from "./MobileHeader";
import BarcodeScannerSheet from "./BarcodeScannerSheet";
import { useAuth } from "../context/AuthContext";
import { useWarehouses } from "../lib/useWarehouses";

const WH_KEY = "samplesWarehouseId";

// iter 267: sample warehouse scan-in. Scan UPC (or type item no) → product
// card with current stock → qty → confirm → back to scanning.
export default function MobileSampleReceive() {
  const { t } = useTranslation();
  const [scannerOpen, setScannerOpen] = useState(false);
  const [code, setCode] = useState("");
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [busy, setBusy] = useState(false);
  const [doneCount, setDoneCount] = useState(0);
  // iter 275: managers are pinned to their warehouse; admins pick one (remembered).
  const { user } = useAuth();
  const { active: warehouses, nameOf } = useWarehouses();
  const isMgr = user?.role === "sample_warehouse";
  const [pickedWh, setPickedWh] = useState(() => localStorage.getItem(WH_KEY) || "");
  const warehouseId = isMgr ? user?.warehouse_id : (pickedWh || warehouses.find((w) => !w.is_default)?.id || warehouses[0]?.id || "");
  const whParams = isMgr ? {} : { warehouse_id: warehouseId };

  const lookup = async (c) => {
    const v = (c || "").trim();
    if (!v) return;
    try {
      const { data } = await api.get("/samples/lookup", { params: { code: v, ...whParams } });
      setProduct(data); setQty(1); setCode(v);
    } catch (e) {
      setProduct(null); setCode(v);
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("samples.no_match"));
    }
  };

  const confirm = async () => {
    if (!product) return;
    setBusy(true);
    try {
      const { data } = await api.post("/samples/receive", { product_id: product.product_id, qty: Number(qty) || 1, note: "scan", ...whParams });
      toast.success(`${data.item_no} → ${data.sample_qty}`);
      setDoneCount((n) => n + 1); setProduct(null); setCode("");
      setScannerOpen(true);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("samples.failed")); } finally { setBusy(false); }
  };

  return (
    <div data-testid="mobile-sample-receive">
      <MobileHeader title={t("samples.receive_title")} subtitle={t("samples.received_count", { count: doneCount })} onBack={true} />
      <div className="px-4 pt-3 space-y-3">
        <div className="flex items-center gap-2 text-xs text-[#78716C]" data-testid="mobile-sample-warehouse">
          <span>{t("samples.receive_into")}</span>
          {isMgr ? (
            <span className="font-semibold text-[#1C1917]" data-testid="mobile-sample-warehouse-name">{nameOf(user?.warehouse_id) || "-"}</span>
          ) : (
            <select value={warehouseId} onChange={(e) => { setPickedWh(e.target.value); localStorage.setItem(WH_KEY, e.target.value); setProduct(null); }} data-testid="mobile-sample-warehouse-select"
              className="h-8 px-2 rounded-lg border border-[#E7E5E4] bg-white text-sm text-[#1C1917] font-medium">
              {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
            </select>
          )}
        </div>
        <button type="button" onClick={() => setScannerOpen(true)} data-testid="mobile-sample-scan-button"
          className="press-scale w-full h-14 rounded-[16px] bg-[#1E3A8A] text-white font-semibold text-base flex items-center justify-center gap-2">
          <BarcodeIcon size={22} weight="bold" /> {t("samples.scan_upc")}
        </button>
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <MagnifyingGlass size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#A8A29E]" />
            <input value={code} onChange={(e) => setCode(e.target.value)} onKeyDown={(e) => e.key === "Enter" && lookup(code)} placeholder={t("samples.code_placeholder")}
              data-testid="mobile-sample-code-input" className="w-full h-11 pl-10 pr-3 rounded-[12px] border border-[#E7E5E4] bg-white text-base font-mono" />
          </div>
          <button type="button" onClick={() => lookup(code)} data-testid="mobile-sample-lookup-button" className="press-scale h-11 px-4 rounded-[12px] border border-[#E7E5E4] bg-white text-sm font-medium text-[#1C1917]">{t("samples.find")}</button>
        </div>

        {product && (
          <div className="bg-white rounded-[16px] border border-[#E7E5E4] p-4 space-y-4" data-testid="mobile-sample-product-card">
            <div className="flex items-center gap-3">
              <div className="w-16 h-16 rounded-[12px] bg-[#FAFAF9] overflow-hidden flex items-center justify-center flex-shrink-0">
                {product.photo_url ? <img src={product.photo_url} alt="" className="w-full h-full object-cover" /> : <ImageBroken size={20} className="text-[#E7E5E4]" />}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-mono text-[#78716C]">{product.item_no}</p>
                <p className="text-sm text-[#1C1917] leading-snug line-clamp-2">{product.description || "-"}</p>
                <p className="text-xs text-[#78716C] mt-1">{t("samples.current")}: <span className={`font-mono font-semibold ${product.sample_qty < 0 ? "text-red-600" : "text-[#1E3A8A]"}`} data-testid="mobile-sample-current-qty">{product.sample_qty}</span></p>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-[#78716C]">{t("samples.receive_qty")}</span>
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => setQty((n) => Math.max(1, Number(n) - 1))} className="press-scale w-10 h-10 rounded-full bg-[#FAFAF9] border border-[#E7E5E4] flex items-center justify-center text-[#78716C]" data-testid="mobile-sample-qty-minus"><Minus size={14} weight="bold" /></button>
                <input type="number" inputMode="numeric" min="1" value={qty} onChange={(e) => setQty(e.target.value)} data-testid="mobile-sample-qty-input" className="w-16 h-10 text-center text-lg font-semibold rounded-lg border border-[#E7E5E4] bg-white" />
                <button type="button" onClick={() => setQty((n) => Number(n) + 1)} className="press-scale w-10 h-10 rounded-full bg-[#1E3A8A] flex items-center justify-center text-white" data-testid="mobile-sample-qty-plus"><Plus size={14} weight="bold" /></button>
              </div>
            </div>
            <button type="button" onClick={confirm} disabled={busy} data-testid="mobile-sample-confirm"
              className="press-scale w-full h-12 rounded-[14px] bg-green-600 text-white font-semibold text-base flex items-center justify-center gap-2 disabled:opacity-70">
              <Check size={18} weight="bold" /> {t("samples.confirm_receive")}
            </button>
          </div>
        )}
      </div>
      <BarcodeScannerSheet open={scannerOpen} onClose={() => setScannerOpen(false)} onScan={(c) => { setScannerOpen(false); lookup(c); }} />
    </div>
  );
}
